import type { RefObject } from 'react'

import { useEffect, useRef } from 'react'

interface ScrollProgressOptions {
    /** Viewport fraction where progress starts, measured against the element's top edge. 1 is the bottom of the screen. */
    start?: number
    /** Viewport fraction where progress reaches 1. */
    end?: number
    /** CSS variable that receives the 0 to 1 value. */
    property?: string
}

const clamp = (value: number): number => Math.min(1, Math.max(0, value))

/**
 * Writes the element's scroll progress to a CSS variable on every animation
 * frame while it is near the viewport. Styles read it with calc(), so React
 * never re-renders while the page scrolls.
 */
export const useScrollProgress = <T extends HTMLElement = HTMLDivElement>({
    start = 1,
    end = 0.3,
    property = '--p'
}: ScrollProgressOptions = {}): RefObject<T | null> => {
    const ref = useRef<T>(null)

    useEffect(() => {
        const node = ref.current
        if (!node) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            node.style.setProperty(property, '1')
            return
        }

        let frame = 0
        let listening = false

        const update = (): void => {
            frame = 0
            const rect = node.getBoundingClientRect()
            const viewport = window.innerHeight || 1
            const from = viewport * start
            const to = viewport * end
            const value = clamp((from - rect.top) / Math.max(1, from - to))
            node.style.setProperty(property, value.toFixed(4))
        }

        const schedule = (): void => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        const listen = (active: boolean): void => {
            if (active === listening) return
            listening = active

            if (active) {
                window.addEventListener('scroll', schedule, { passive: true })
                window.addEventListener('resize', schedule)
                schedule()
            }
            else {
                window.removeEventListener('scroll', schedule)
                window.removeEventListener('resize', schedule)
            }
        }

        update()

        if (typeof IntersectionObserver === 'undefined') {
            listen(true)
            return () => listen(false)
        }

        const observer = new IntersectionObserver(([entry]) => {
            listen(entry.isIntersecting)
            if (!entry.isIntersecting) update()
        }, { rootMargin: '25% 0px 25% 0px' })

        observer.observe(node)

        return () => {
            observer.disconnect()
            listen(false)
            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [start, end, property])

    return ref
}
